import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { IconArrowRight } from '@components/icons/arrow-right';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { useStore } from 'models/Provider';

const color = 'hsl(243 89% 33%)';

export const PromoteHIExample = observer((props) => {
  // @ts-ignore-next-line
  const { job, state, boardState, title } = props;
  const { current } = useStore();
  const deadline = abbreviateDate(job.deadline, 'is');

  const ratioIncludesPercentage = boardState.ratio.endsWith('%');
  const ratioString = ratioIncludesPercentage ? boardState.ratio : `${boardState.ratio}%`;

  return (
    <Container>
      <link
        href="https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap"
        rel="stylesheet"
      ></link>
      <RowContainer align="center">
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img src={current.info.logo} alt={title} />
        </div>
        <Stack space={6}>
          <Text
            text={boardState.title || job.title}
            style={{
              color,
              fontSize: pxToRem(27),
              fontFamily: '"Atkinson Hyperlegible", sans-serif',
              fontWeight: 700,
            }}
          />
          <Stack orientation="horizontal" space={2}>
            <Tag text={`Frestur ${deadline}`} />
            <Tag text={ratioString} />
            <Tag text={state.data.title} />
          </Stack>
          <Stack orientation="horizontal" space={6} justify="flex-end">
            <ActionButton
              text="Sækja um"
              iconRight={<IconArrowRight color="white100" />}
              style={{
                backgroundColor: color,
              }}
            />
            <ActionButton
              text="Til baka"
              iconRight={<IconArrowRight color="blueFb100" />}
              style={{
                backgroundColor: 'white',
                border: `solid ${pxToRem(2)} ${color}`,
                color,
              }}
            />
          </Stack>
        </Stack>
      </RowContainer>
      <RowContainer>
        <FancyBox>
          <Title text="Upplýsingar" />
          <div dangerouslySetInnerHTML={{ __html: state.data.description }} />
        </FancyBox>
        <FancyBox>
          <Stack space={4}>
            <Stack space={3} orientation="horizontal">
              <Property title="Vinnuveitandi" value={current.info.companyName} />
              <Property title="Starfsheiti" value={state.data.title} />
              <Property title="Frestur" value={deadline} />
              <Property title="Starfshlutfall" value={ratioString} />
            </Stack>
            <div>
              <Title text="Hæfniskröfur" />
              <div dangerouslySetInnerHTML={{ __html: boardState.requirements }} />
            </div>
          </Stack>
        </FancyBox>
      </RowContainer>
    </Container>
  );
});

type PropertyProps = {
  title: string;
  value: string;
};

const Property = ({ title, value }: PropertyProps) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Text
        text={title}
        style={{
          color,
          fontWeight: 700,
        }}
      />
      <Text
        text={value}
        style={{
          color,
          marginTop: pxToRem(-4),
        }}
      />
    </div>
  );
};

const RowContainer = styled(Stack).attrs({
  space: 6,
  orientation: 'horizontal',
})({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gap: pxToRem(24),
  padding: `${pxToRem(32)} 0`,
});

const Tag = styled(Text)({
  color,
  fontWeight: 700,
  fontSize: 14,
  backgroundColor: '#e6edff',
  width: 'fit-content',
  padding: `0 ${pxToRem(4)}`,
});

const ActionButton = styled(Button)({
  fontWeight: 700,
  width: pxToRem(140),
  borderRadius: pxToRem(2),
});

const Title = styled(Text)({
  fontSize: 18,
  fontWeight: 700,
});

const FancyBox = styled.div({
  boxShadow: '0px 0px 40px 0px rgba(89,89,89,.08)',
  padding: `${pxToRem(16)} ${pxToRem(24)}`,
  borderBottom: `${pxToRem(5)} solid #bf125d`,
});

const Container = styled.div({
  background: '#fff',
  padding: pxToRem(50),
  '*': {
    fontFamily: '"Atkinson Hyperlegible", sans-serif',
  },
});
